import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { formatPlate } from '@/lib/carwash/plate'

const SIZES = {
    sm: 'h-7 text-[13px] pr-2 gap-1.5 rounded-md',
    md: 'h-8 text-[15px] pr-2.5 gap-2 rounded-md',
    lg: 'h-11 text-xl pr-3.5 gap-2.5 rounded-lg',
    xl: 'h-14 text-[26px] pr-4 gap-3 rounded-xl'
}

const STRIP = {
    sm: 'w-1.5',
    md: 'w-2',
    lg: 'w-2.5',
    xl: 'w-3'
}

interface PlateProps {
    value: string
    size?: keyof typeof SIZES
    className?: string
}

/* A number plate styled after Indian registration plates. */
export const Plate: FC<PlateProps> = ({
    value,
    size = 'md',
    className
}): ReactNode => {
    const formatted = formatPlate(value)

    if (!formatted) {
        return (
            <span
                className={cn(
                    'inline-flex items-center border border-dashed border-slate-300 bg-slate-50 px-2.5 font-heading font-bold tracking-wide text-slate-500 uppercase',
                    SIZES[size],
                    className
                )}
            >
                No number
            </span>
        )
    }

    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-stretch overflow-hidden border border-slate-800/80 bg-white font-heading font-extrabold tracking-[0.06em] whitespace-nowrap text-slate-950 shadow-[inset_0_-1px_0_rgb(15_23_42/0.08)]',
                SIZES[size],
                className
            )}
        >
            <span className={cn('shrink-0 bg-primary', STRIP[size])} aria-hidden='true' />
            <span className='flex items-center'>
                {formatted}
            </span>
        </span>
    )
}
